import {
  DiscoveryApi,
  FetchApi,
  OpenIdConnectApi,
} from '@backstage/core-plugin-api';
import { Entity } from '@backstage/catalog-model';
import { CatalogApi } from '@backstage/catalog-client';
import { GCPResourceAnnotations } from '@internal/backstage-plugin-gcp-common';
import {
  AccountView,
  Environment,
  NewWorkspaceCommand,
  WorkspaceDetailResponse,
  WorkspaceItem,
  UpdateWorkspaceCommand,
  CatalogData,
  Provision,
  ProvisionSubTask,
  ProvisionTask,
  MigrationPeriod,
  PageResponse,
  WorkspaceSystemItem,
  PageFilter,
  RetrySystemProvisionCommand,
  AddonComponent,
  WorkspaceFilters,
  SimpleWorkspace,
  SystemWorkspace,
  SystemWorkspacesFilter,
  ReprocessAddon,
  ComponentType,
} from '../pages/workspaces/types';
import { ANNOTATION_SYSTEM_APPCODE } from '@internal/backstage-plugin-supply-admin-common';

const DEFAULT_PAGE_SIZE = 50;
const DEFAULT_PAGE_NUMBER = 0;
const ERROR_DETAIL_MAX_LENGTH = 200;

type ResourceInfo = { name: string; resourceRef: string; type: ComponentType[] };

export class WorkspacesService {
  constructor(
    private readonly fetchApi: FetchApi,
    private readonly authApi: OpenIdConnectApi,
    private readonly discoveryApi: DiscoveryApi,
    private readonly catalogApi: CatalogApi,
  ) { }

  private async safeJsonParse<T>(response: Response): Promise<T> {
    const contentType = response.headers.get('content-type');

    if (
      !contentType?.includes('application/json') ||
      (response.status >= 204 && response.status <= 205)
    ) {
      throw new Error(
        `Unexpected response format: ${response.status} ${response.statusText}`,
      );
    }

    try {
      return (await response.json()) as T;
    } catch (parseError) {
      console.error('JSON parse failed:', parseError);
      throw new Error('Invalid response format from server');
    }
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const errorDetail = await this.extractErrorDetail(response);

      console.error(`API request failed [${response.status}]:`, {
        status: response.status,
        statusText: response.statusText,
        detail: errorDetail?.substring(0, ERROR_DETAIL_MAX_LENGTH),
      });

      throw new Error(errorDetail);
    }

    return this.safeJsonParse<T>(response);
  }

  private async extractErrorDetail(response: Response): Promise<string> {
    const fallback = response.statusText || `Request failed with status ${response.status}`;

    try {
      const body = await response.text();
      const trimmedBody = body?.trim();

      if (!trimmedBody) return fallback;

      // Try first JSON parse
      let parsed: unknown;
      try {
        parsed = JSON.parse(trimmedBody);
      } catch {
        // Body is not JSON — return raw text if short enough
        return trimmedBody.length <= ERROR_DETAIL_MAX_LENGTH ? trimmedBody : fallback;
      }

      // First parse returned a plain string: possibly double-encoded JSON
      if (typeof parsed === 'string') {
        const innerStr = parsed.trim();

        if (!innerStr) return fallback;

        // Try second parse
        let innerParsed: unknown;
        try {
          innerParsed = JSON.parse(innerStr);
        } catch {
          // Inner value is not JSON — return it as-is
          return innerStr.length <= ERROR_DETAIL_MAX_LENGTH ? innerStr : fallback;
        }

        // Second parse also returned a plain string — use it directly
        if (typeof innerParsed === 'string') {
          const finalStr = innerParsed.trim();
          return finalStr.length <= ERROR_DETAIL_MAX_LENGTH ? finalStr : fallback;
        }

        // Second parse returned an object — continue with it
        parsed = innerParsed;
      }

      // Extract message from JSON object
      if (typeof parsed === 'object' && parsed !== null) {
        const obj = parsed as {
          message?: string;
          error?: string | { message?: string };
        };

        // Upstream API format: { message: "..." }
        if (obj.message) return obj.message;

        // Backstage error handler format: { error: { message: "..." } }
        if (obj.error && typeof obj.error === 'object' && obj.error.message) {
          return obj.error.message;
        }

        // Backstage simple format: { error: "..." }
        if (typeof obj.error === 'string' && obj.error) {
          return obj.error;
        }
      }
    } catch (e) {
      console.error('[WorkspacesService] extractErrorDetail failed', e);
    }

    return fallback;
  }

  private async buildBaseUrl(env: Environment): Promise<string> {
    const baseUrl = await this.discoveryApi.getBaseUrl('supply-features');
    const normalizedBase = baseUrl.endsWith('/')
      ? baseUrl.slice(0, -1)
      : baseUrl;
    return `${normalizedBase}/open-control/${env}`;
  }

  private async getAuthHeaders(): Promise<Headers> {
    try {
      const token = await this.authApi.getIdToken();

      if (!token) {
        throw new Error(
          'Authentication token not available. Please log in again.',
        );
      }

      const headers = new Headers();
      headers.set('X-User-Token', `Bearer ${token}`);
      return headers;
    } catch (originalError) {
      const authError = new Error(
        'Failed to authenticate request. Please log in again.',
      );
      (authError as Error & { cause?: unknown }).cause = originalError;
      console.error('Auth headers error:', originalError);
      throw authError;
    }
  }

  public async searchAccounts(
    environment: Environment,
    searchParams?: { search?: string; page?: number; pageSize?: number },
  ): Promise<PageResponse<AccountView>> {
    const baseUrl = await this.buildBaseUrl(environment);
    const url = new URL(`${baseUrl}/accounts`);
    const headers = await this.getAuthHeaders();

    url.searchParams.append('page', String(searchParams?.page ?? 1));
    url.searchParams.append('pageSize', String(searchParams?.pageSize ?? 100));

    if (searchParams?.search?.trim()) {
      url.searchParams.append('search', searchParams.search.trim());
    }

    try {
      const response = await this.fetchApi.fetch(url.toString(), { headers });
      return await this.handleResponse<PageResponse<AccountView>>(response);
    } catch (error) {
      console.error('Error fetching accounts:', error);
      throw error instanceof Error
        ? error
        : new Error('Falha ao buscar accounts.');
    }
  }

  public async searchWorkspacesByAccount(
    accountId: string,
    environment: Environment,
    searchParams?: {
      name?: string;
      document?: string;
      page?: number;
      pageSize?: number;
    },
  ): Promise<PageResponse<WorkspaceItem>> {
    const baseUrl = await this.buildBaseUrl(environment);
    const url = new URL(`${baseUrl}/accounts/${accountId}/workspaces`);
    const headers = await this.getAuthHeaders();

    url.searchParams.append('page', String(searchParams?.page ?? 1));
    url.searchParams.append('pageSize', String(searchParams?.pageSize ?? 100));

    if (searchParams?.name?.trim()) {
      url.searchParams.append('name', searchParams.name.trim());
    }
    if (searchParams?.document?.trim()) {
      url.searchParams.append('document', searchParams.document.trim());
    }

    try {
      const response = await this.fetchApi.fetch(url.toString(), { headers });
      return await this.handleResponse<PageResponse<WorkspaceItem>>(response);
    } catch (error) {
      console.error('Error fetching workspaces:', error);
      throw error instanceof Error
        ? error
        : new Error('Falha ao buscar workspaces.');
    }
  }

  public async getWorkspaceDetails(
    environment: Environment,
    workspaceId: string,
  ): Promise<WorkspaceDetailResponse> {
    const baseUrl = await this.buildBaseUrl(environment);
    const url = `${baseUrl}/workspaces/${workspaceId}`;
    const headers = await this.getAuthHeaders();

    try {
      const response = await this.fetchApi.fetch(url, { headers });
      return await this.handleResponse<WorkspaceDetailResponse>(response);
    } catch (error) {
      console.error('Error fetching workspace details:', error);
      throw error instanceof Error
        ? error
        : new Error('Falha ao buscar detalhes da workspace.');
    }
  }

  public async getWorkspaces(
    environment: Environment,
    filter: WorkspaceFilters,
  ): Promise<PageResponse<SimpleWorkspace>> {
    const baseUrl = await this.buildBaseUrl(environment);
    const url = new URL(`${baseUrl}/workspaces`);
    url.searchParams.append('systemId', filter.systemId);
    url.searchParams.append('addonId', filter.addonId);
    if (filter.partialRacId) {
      url.searchParams.append('partialRacId', filter.partialRacId);
    }
    if (filter.partialName) {
      url.searchParams.append('partialName', filter.partialName);
    }
    url.searchParams.append('page', '1');
    url.searchParams.append('pageSize', '25');

    const headers = await this.getAuthHeaders();

    try {
      const response = await this.fetchApi.fetch(url.toString(), { headers });
      return await this.handleResponse<PageResponse<SimpleWorkspace>>(response);
    } catch (error) {
      throw error instanceof Error
        ? error
        : new Error('Falha ao buscar workspaces.');
    }
  }

  /**
   * Extracts the supplyadmin system id (appcode, e.g. "log004") from a
   * catalog System entity. Returns undefined when the annotation is absent.
   */
  public static getSystemId(entity?: Entity): string | undefined {
    return entity?.metadata.annotations?.[
      ANNOTATION_SYSTEM_APPCODE
    ]?.toLowerCase();
  }

  /**
   * Maps a SystemWorkspace to the option shape used by workspace selectors,
   * keeping the same display format the catalog entities had
   * (`Name (subdomain - tenant)`). The API exposes the tenant as `racId`.
   */
  public static toWorkspaceOption(workspace: SystemWorkspace): {
    id: string;
    name: string;
    tenant: string;
  } {
    return {
      id: workspace.workspaceId,
      name: `${workspace.name} (${workspace.subdomain} - ${workspace.racId})`,
      tenant: workspace.racId,
    };
  }

  public async getWorkspacesBySystem(
    environment: Environment,
    systemId: string,
    filter: SystemWorkspacesFilter = {},
  ): Promise<PageResponse<SystemWorkspace>> {
    const baseUrl = await this.buildBaseUrl(environment);
    const url = new URL(
      `${baseUrl}/workspaces/system/${encodeURIComponent(systemId)}`,
    );

    url.searchParams.append('page', String(filter.page ?? 1));
    url.searchParams.append('pageSize', String(filter.pageSize ?? 100));

    if (filter.partialRacId?.trim()) {
      url.searchParams.append('partialRacId', filter.partialRacId.trim());
    }
    if (filter.partialName?.trim()) {
      url.searchParams.append('partialName', filter.partialName.trim());
    }
    if (filter.partialSubdomain?.trim()) {
      url.searchParams.append('partialSubdomain', filter.partialSubdomain.trim());
    }

    const headers = await this.getAuthHeaders();

    try {
      const response = await this.fetchApi.fetch(url.toString(), { headers });
      return await this.handleResponse<PageResponse<SystemWorkspace>>(response);
    } catch (error) {
      console.error('Error fetching system workspaces:', error);
      throw error instanceof Error
        ? error
        : new Error('Failed to fetch system workspaces.');
    }
  }

  /**
   * Fetches every page of workspaces for a system.
   */
  public async getAllWorkspacesBySystem(
    environment: Environment,
    systemId: string,
    filter: Omit<SystemWorkspacesFilter, 'page' | 'pageSize'> = {},
  ): Promise<SystemWorkspace[]> {
    const pageSize = 100;
    const workspacesByRacId = new Map<string, SystemWorkspace>();
    let page = 1;
    let hasNext = true;

    while (hasNext) {
      const response = await this.getWorkspacesBySystem(environment, systemId, {
        ...filter,
        page,
        pageSize,
      });
      // The open-control API's pagination can return the same workspace on
      // more than one page (e.g. when the underlying dataset shifts between
      // requests), so entries are deduplicated by racId here. Downstream
      // consumers rely on racId as a stable React key, and duplicate keys
      // make React's list reconciliation leave stale DOM nodes behind.
      for (const item of response.items) {
        workspacesByRacId.set(item.racId, item);
      }
      hasNext = response.hasNext && response.items.length > 0;
      page += 1;
    }

    return [...workspacesByRacId.values()];
  }

  public async getWorkspaceSystems(
    environment: Environment,
    workspaceId: string,
  ): Promise<PageResponse<WorkspaceSystemItem>> {
    const baseUrl = await this.buildBaseUrl(environment);
    const url = new URL(`${baseUrl}/workspaces/${workspaceId}/systems`);
    url.searchParams.append('page', '1');
    url.searchParams.append('pageSize', '100');
    const headers = await this.getAuthHeaders();

    try {
      const response = await this.fetchApi.fetch(url.toString(), { headers });
      return await this.handleResponse<PageResponse<WorkspaceSystemItem>>(response);
    } catch (error) {
      console.error('Error fetching workspace systems:', error);
      throw error instanceof Error
        ? error
        : new Error('Falha ao buscar sistemas da workspace.');
    }
  }

  public async updateWorkspace(
    environment: Environment,
    workspaceId: string,
    updateCommand: UpdateWorkspaceCommand,
  ): Promise<void> {
    const baseUrl = await this.buildBaseUrl(environment);
    const url = `${baseUrl}/workspaces/${workspaceId}`;
    const headers = await this.getAuthHeaders();
    headers.set('Content-Type', 'application/json');

    try {
      const response = await this.fetchApi.fetch(url, {
        method: 'PUT',
        headers,
        body: JSON.stringify(updateCommand),
      });
      await this.handleResponse<void>(response);
    } catch (error) {
      console.error('Error updating workspace:', error);
      throw error instanceof Error
        ? error
        : new Error('Falha ao atualizar workspace.');
    }
  }

  public async addWorkspaceSystem(
    environment: Environment,
    workspaceId: string,
    addCommand: CatalogData,
  ): Promise<void> {
    const baseUrl = await this.buildBaseUrl(environment);
    const url = `${baseUrl}/provisions/workspace/${workspaceId}/system`;
    const headers = await this.getAuthHeaders();
    headers.set('Content-Type', 'application/json');

    try {
      const response = await this.fetchApi.fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(addCommand),
      });
      await this.handleResponse<void>(response);
    } catch (error) {
      throw error instanceof Error
        ? error
        : new Error('Error adding system to workspace.');
    }
  }

  public async removeWorkspaceSystem(
    environment: Environment,
    workspaceId: string,
    removeCommand: CatalogData,
  ): Promise<void> {
    const baseUrl = await this.buildBaseUrl(environment);
    const url = `${baseUrl}/provisions/workspace/${workspaceId}/system`;
    const headers = await this.getAuthHeaders();
    headers.set('Content-Type', 'application/json');

    try {
      const response = await this.fetchApi.fetch(url, {
        method: 'DELETE',
        headers,
        body: JSON.stringify(removeCommand),
      });
      await this.handleResponse<void>(response);
    } catch (error) {
      throw error instanceof Error
        ? error
        : new Error('Error removing system from workspace.');
    }
  }

  private async manageAddon(
    type: 'provision' | 'unprovision',
    environment: Environment,
    workspaceId: string,
    payload: CatalogData,
  ): Promise<void> {
    const baseUrl = await this.buildBaseUrl(environment);
    const url = `${baseUrl}/${type}s/workspace/${workspaceId}/addon`;
    const headers = await this.getAuthHeaders();
    headers.set('Content-Type', 'application/json');

    try {
      const response = await this.fetchApi.fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });
      await this.handleResponse<void>(response);
    } catch (error) {
      throw error instanceof Error
        ? error
        : new Error(`Error ${type}ing addon.`);
    }
  }

  public async provisionAddon(
    environment: Environment,
    workspaceId: string,
    payload: CatalogData,
  ): Promise<void> {
    return this.manageAddon('provision', environment, workspaceId, payload);
  }

  public async unprovisionAddon(
    environment: Environment,
    workspaceId: string,
    payload: CatalogData,
  ): Promise<void> {
    return this.manageAddon('unprovision', environment, workspaceId, payload);
  }

  public async getProvisions(
    environment: Environment,
    workspaceId: string,
    pageRequest: PageFilter,
    status?: string,
  ): Promise<PageResponse<Provision>> {
    const baseUrl = await this.buildBaseUrl(environment);
    const url = new URL(`${baseUrl}/provisions`);
    const headers = await this.getAuthHeaders();

    url.searchParams.append('workspaceId', workspaceId);
    if (status)
      url.searchParams.append('status', status);
    url.searchParams.append('page', pageRequest.page.toString());
    url.searchParams.append('pageSize', pageRequest.pageSize.toString());

    try {
      const response = await this.fetchApi.fetch(url.toString(), { headers });
      return await this.handleResponse<PageResponse<Provision>>(response);
    } catch (error) {
      console.error('Error fetching provisions:', error);
      throw error instanceof Error
        ? error
        : new Error('Falha ao buscar provisionamentos.');
    }
  }

  public async newWorkspaceProvision(
    environment: Environment,
    command: NewWorkspaceCommand,
  ): Promise<void> {
    const baseUrl = await this.buildBaseUrl(environment);
    const url = `${baseUrl}/provisions/workspace`;
    const headers = await this.getAuthHeaders();
    headers.set('Content-Type', 'application/json');

    try {
      const response = await this.fetchApi.fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(command),
      });
      await this.handleResponse<void>(response);
    } catch (error) {
      console.error('Error provisioning workspace:', error);
      throw error instanceof Error
        ? error
        : new Error('Falha ao provisionar workspace.');
    }
  }

  public async listProvisions(
    environment: Environment,
    params: {
      workspaceId?: string;
      workspaceName?: string;
      status?: string;
      system?: string;
      page?: number;
      size?: number;
    } = {},
  ): Promise<PageResponse<Provision>> {
    const baseUrl = await this.buildBaseUrl(environment);
    const url = new URL(`${baseUrl}/provisions`);
    const headers = await this.getAuthHeaders();

    const filters = [
      { key: 'workspaceId', value: params.workspaceId },
      { key: 'workspaceName', value: params.workspaceName },
      { key: 'status', value: params.status },
      { key: 'system', value: params.system },
    ];

    filters.forEach(({ key, value }) => {
      if (value?.trim()) {
        url.searchParams.append(key, value.trim());
      }
    });

    const page = params.page ?? DEFAULT_PAGE_NUMBER;
    const size = params.size ?? 10;
    url.searchParams.append('page', String(page));
    url.searchParams.append('pageSize', String(size));

    try {
      const response = await this.fetchApi.fetch(url.toString(), { headers });
      const data = await this.handleResponse<PageResponse<Provision>>(
        response,
      );
      return data;
    } catch (error) {
      console.error('Error listing provisions:', error);
      throw error instanceof Error
        ? error
        : new Error('Falha ao buscar requests de provisionamento.');
    }
  }

  public async getProvisionTasks(
    environment: Environment,
    provisionId: string,
    params: {
      status?: string;
      system?: string;
      page?: number;
      size?: number;
    } = {},
  ): Promise<PageResponse<ProvisionTask>> {
    const baseUrl = await this.buildBaseUrl(environment);
    const url = new URL(`${baseUrl}/provisions/${provisionId}/tasks`);
    const headers = await this.getAuthHeaders();

    if (params.status) url.searchParams.append('status', params.status);
    if (params.system) url.searchParams.append('system', params.system);

    const page = params.page ?? DEFAULT_PAGE_NUMBER;
    const size = params.size ?? DEFAULT_PAGE_SIZE;
    url.searchParams.append('page', String(page));
    url.searchParams.append('pageSize', String(size));

    try {
      const response = await this.fetchApi.fetch(url.toString(), { headers });
      const data = await this.handleResponse<PageResponse<ProvisionTask>>(
        response,
      );
      return data;
    } catch (error) {
      console.error('Error fetching provision tasks:', error);
      throw error instanceof Error
        ? error
        : new Error('Falha ao buscar tasks do provisionamento.');
    }
  }

  public async getProvisionSubTasks(
    environment: Environment,
    provisionTaskId: string,
    params: {
      status?: string;
      component?: string;
      page?: number;
      size?: number;
    } = {},
  ): Promise<PageResponse<ProvisionSubTask>> {
    const baseUrl = await this.buildBaseUrl(environment);
    const url = new URL(
      `${baseUrl}/provisions/tasks/${provisionTaskId}/subtasks`,
    );
    const headers = await this.getAuthHeaders();

    if (params.status) url.searchParams.append('status', params.status);
    if (params.component) url.searchParams.append('component', params.component);

    const page = params.page ?? DEFAULT_PAGE_NUMBER;
    const size = params.size ?? DEFAULT_PAGE_SIZE;
    url.searchParams.append('page', String(page));
    url.searchParams.append('size', String(size));

    try {
      const response = await this.fetchApi.fetch(url.toString(), { headers });
      const data = await this.handleResponse<PageResponse<ProvisionSubTask>>(
        response,
      );
      return data;
    } catch (error) {
      console.error('Error fetching provision subtasks:', error);
      throw error instanceof Error
        ? error
        : new Error('Falha ao buscar subtasks do provisionamento.');
    }
  }

  public async startIdpMigrationPeriod(
    environment: Environment,
    workspaceId: string,
    migrationPeriod: MigrationPeriod,
  ): Promise<void> {
    const baseUrl = await this.buildBaseUrl(environment);
    const url = `${baseUrl}/workspaces/${workspaceId}/start-auth-migration`;
    const headers = await this.getAuthHeaders();
    headers.set('Content-Type', 'application/json');

    const formatDate = (date: Date) =>
      date.toISOString().slice(0, 10);

    const payload = {
      period: `[${formatDate(migrationPeriod.start)}, ${formatDate(migrationPeriod.end)}]`,
    };

    try {
      const response = await this.fetchApi.fetch(url, {
        method: 'PUT',
        headers,
        body: JSON.stringify(payload),
      });
      await this.handleResponse<void>(response);
    } catch (error: any) {
      throw new Error('Failed to start IDP migration period. Please, try again.');
    }
  }

  public async getInsightComponents(environment: Environment, entity?: Entity | null): Promise<AddonComponent[]> {
    const resources = this.extractInsightsDatabaseInfo(environment!, entity);

    return Promise.all([
      ...resources.map((resource) => this.fetchAddonComponentDetails(resource)),
    ]);
  }

  public extractInsightsDatabaseInfo(environment: Environment, entity?: Entity | null): ResourceInfo[] {
    if (!entity) {
      return [];
    }
    
    const insightsResources = this.extractDatabaseInfo(environment, entity, 'INSIGHTS');
    const datasharingResources = this.extractDatabaseInfo(environment, entity, 'DATA_SHARING');

    return this.mergeResourceInfos(insightsResources, datasharingResources);
  }

  private extractDatabaseInfo(environment: Environment, entity: Entity, type: ComponentType): ResourceInfo[] {
    const annotation = type === 'INSIGHTS' 
      ? 'open.totvs.io/insights-databases' 
      : 'open.totvs.io/datasharing-databases';


    return entity?.metadata.annotations?.[annotation]
      ?.split(',')
      ?.map(item => item.trim())
      ?.filter(item => !!item && item.includes(environment))
      ?.map((resource: string) => {
        const [name, resourceRef] = resource.split('|').map((part: string) => part.trim());
        return {
          name,
          resourceRef,
          type: [type],
        };
      }) || [];
  }

  private mergeResourceInfos(insights: ResourceInfo[], datasharing: ResourceInfo[]): ResourceInfo[] {
    return [...insights, ...datasharing].reduce<ResourceInfo[]>((acc, resource) => {
      const existing = acc.find(r => r.name === resource.name && r.resourceRef === resource.resourceRef);

      if (existing) {
        existing.type = [...new Set([...existing.type, ...resource.type])];
        return acc;
      }

      return [...acc, { ...resource }];
    }, []);
  }

  private async fetchAddonComponentDetails(resourceInfo: ResourceInfo): Promise<AddonComponent> {
    try {
      const { name, resourceRef } = resourceInfo;

      const database = await this.catalogApi.getEntityByRef(resourceRef);
      if (!database) {
        throw new Error('Failed to fetch insights addon.');
      }

      const dependsOn = database.spec?.dependsOn;
      const instanceRef = Array.isArray(dependsOn) ? (dependsOn as string[])[0] : undefined;

      const instance = instanceRef
        ? await this.catalogApi.getEntityByRef(instanceRef)
        : undefined;

      const externalIp = instance?.metadata.annotations?.[GCPResourceAnnotations.SQL_INSTANCE_IP_PRIMARY];
      const internalIp = instance?.metadata.annotations?.[GCPResourceAnnotations.SQL_INSTANCE_IP_PRIVATE];
      
      return {
        name: name,
        ref: resourceRef,
        databaseName: database.metadata.name,
        type: resourceInfo.type,
        internalUrl: `${internalIp}:5432/${database.metadata.name}`,
        externalUrl: `${externalIp}:5432/${database.metadata.name}`,
      };
    } catch(err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch insights addon.';
      throw new Error(message);
    }
  }

  public async retryWorkspaceProvision(
    environment: Environment,
    workspaceId: string,
  ): Promise<void> {
    const baseUrl = await this.buildBaseUrl(environment);
    const url = `${baseUrl}/provisions/workspace/${workspaceId}/retry`;
    const headers = await this.getAuthHeaders();
    headers.set('Content-Type', 'application/json');

    try {
      const response = await this.fetchApi.fetch(url, {
        method: 'POST',
        headers,
      });
      await this.handleResponse<void>(response);
    } catch (error) {
      console.error('Error retrying workspace provision:', error);
      throw error instanceof Error
        ? error
        : new Error('Falha ao tentar reprocessar o provisionamento da workspace.');
    }
  }

  public async retrySystemProvision(
    environment: Environment,
    workspaceId: string,
    command: RetrySystemProvisionCommand,
  ): Promise<void> {
    const baseUrl = await this.buildBaseUrl(environment);
    const url = `${baseUrl}/provisions/workspace/${workspaceId}/system/retry`;
    const headers = await this.getAuthHeaders();
    headers.set('Content-Type', 'application/json');

    try {
      const response = await this.fetchApi.fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(command),
      });
      await this.handleResponse<void>(response);
    } catch (error) {
      console.error('Error retrying system provision:', error);
      throw error instanceof Error
        ? error
        : new Error('Falha ao tentar reprocessar o provisionamento do sistema.');
    }
  }

  public async reprocessTables(environment: Environment, command: ReprocessAddon): Promise<void> {
    const baseUrl = await this.buildBaseUrl(environment);
    const url = `${baseUrl}/provisions/addon/reprovision`;
    const headers = await this.getAuthHeaders();
    headers.set('Content-Type', 'application/json');

    try {
      const response = await this.fetchApi.fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(command),
      });
      await this.handleResponse<void>(response);
    } catch (error) {
      throw error instanceof Error
        ? error
        : new Error('Failed to reprocess tables. Please, try again later.');
    }
  }
}

export type {
  AccountView,
  WorkspaceItem,
  WorkspaceDetailResponse,
  UpdateWorkspaceCommand,
  CatalogData,
  PageFilter,
  Provision,
  ProvisionTask,
  ProvisionSubTask,
  RetrySystemProvisionCommand,
};
